/**
 * Which tabs the product edit dialog offers, and in what order.
 *
 * The tab list is the single gate on what the dialog can show: the body renders whichever tab is
 * active, and the active tab is always one of these. Not everyone may see product attributes
 * (`useHasProductAttributeAccess`), so an attribute tab being absent here is what keeps attributes
 * out of reach (PLT-6329).
 *
 * "Basic information" is always first, for leaf products and for segments alike: the dialog is the
 * only place a row's name and description can be edited (PLT-6331).
 */
export enum ProductEditDialogTab {
  BASICS = 'basics',
  ATTRIBUTES = 'attributes',
  ADVANCED = 'advanced',
  MATCHING = 'matching',
}

interface VisibleTabsParams {
  /**
   * The segment editor drops the "Matching" tab: matching is team-only there and has its own
   * "Edit matching" modal reached from the row kebab.
   */
  segmentEditorLayout: boolean;
  /** Whether the viewer may see product attributes here (`useHasProductAttributeAccess`). */
  hasAttributeAccess: boolean;
  /** Taxonomy matching is internal-only. */
  isTeam: boolean;
}

/**
 * Non-empty by construction: the caller falls back to the first tab when the viewer's selection
 * isn't on offer, so an empty list would leave the dialog with no active tab and an empty body.
 */
export function getVisibleProductEditDialogTabs({
  segmentEditorLayout,
  hasAttributeAccess,
  isTeam,
}: VisibleTabsParams): [ProductEditDialogTab, ...ProductEditDialogTab[]] {
  // Team users always have attribute access. The access hook already resolves it that way,
  // but the invariant is restated here so this function cannot be handed a contradictory input and
  // answer with a tab strip that has a hole where the attribute tabs should be.
  const canSeeAttributes = hasAttributeAccess || isTeam;
  return [
    ProductEditDialogTab.BASICS,
    ...(canSeeAttributes ? [ProductEditDialogTab.ATTRIBUTES, ProductEditDialogTab.ADVANCED] : []),
    ...(isTeam && !segmentEditorLayout ? [ProductEditDialogTab.MATCHING] : []),
  ];
}
